import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";

const PILLAR_ART = "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts";

// Playlists are editable here without a rebuild is required in code.
// Later this same shape can be persisted in the admin panel / config store.
//   playlistUrl:  full Spotify or YouTube URL (empty = "Coming soon" state)
//   playlistType: "auto" | "youtube" | "spotify" — "auto" detects from the URL
const PILLARS = {
  worship: {
    letter: "W",
    title: "Worship",
    image: `${PILLAR_ART}/78wwem06_pillar-worship.png`,
    scripture: {
      ref: "John 4:23-24 NKJV",
      text: "\u201cBut the hour is coming, and now is, when the true worshipers will worship the Father in spirit and truth; for the Father is seeking such to worship Him. God is Spirit, and those who worship Him must worship in spirit and truth.\u201d",
    },
    teaching: [
      "Worship is not a slot on the Sunday order of service — it is the atmosphere of a life fully surrendered to Jesus. Before we lift our hands, we lift our hearts. Before we sing a note, our lives sing a song.",
      "God is not looking for performance. He is seeking worshipers. When we come to Him in spirit — with a soft, honest, fully-alive heart — and in truth — anchored in His Word and the finished work of Jesus — we step into the reason we were made.",
      "At WHCO, worship shapes everything else. It is the fuel of our prayer, the doorway to His presence, and the posture we carry into our workplaces, homes, and streets.",
    ],
    livesLike: [
      "Daily private worship before public gatherings.",
      "Undignified, uninhibited praise on Sunday mornings.",
      "Worship as warfare — the atmosphere shifts when Jesus is exalted.",
    ],
    playlistUrl: "",
    playlistType: "auto",
  },
  holiness: {
    letter: "H",
    title: "Holiness",
    image: `${PILLAR_ART}/t7c792q7_pillar-holiness.png`,
    scripture: {
      ref: "II Corinthians 7:1 NKJV",
      text: "\u201cTherefore, having these promises, beloved, let us cleanse ourselves from all filthiness of the flesh and spirit, perfecting holiness in the fear of God.\u201d",
    },
    teaching: [
      "Holiness is not a list of things we cannot do. Holiness is being set apart for the One we love. Because we belong to Jesus, everything about us — mind, body, mouth, motives — is being conformed to His image.",
      "The fear of the Lord is the beginning of wisdom. It is not the fear of a slave, but the reverence of a son who refuses to grieve the Father. It gives us clean hands and a pure heart in a generation that celebrates compromise.",
      "At WHCO, we do not moralize — we pursue Jesus. And the closer we walk with Him, the more the things that once held us lose their grip.",
    ],
    livesLike: [
      "Repentance as a lifestyle, not a last resort.",
      "Guarding what we watch, say, and consume.",
      "Accountability inside real relationships.",
    ],
    playlistUrl: "",
    playlistType: "auto",
  },
  covenant: {
    letter: "C",
    title: "Covenant",
    image: `${PILLAR_ART}/wtlcqq8j_pillar-covenant.png`,
    scripture: {
      ref: "Psalm 50:5 NKJV",
      text: "\u201cGather My saints together to Me, those who have made a covenant with Me by sacrifice.\u201d",
    },
    teaching: [
      "Covenant is God\u2019s idea. He is the God who calls His people together and binds them to Himself by sacrifice — first at the altar of old, ultimately in the blood of Jesus. To be His saint is to belong to a covenant, not just a crowd.",
      "Because covenant is deeper than contract and stronger than feeling, it holds when the storms come. We do not merely attend a church — we belong to a family: showing up when it is easy, showing up when it is hard, speaking the truth in love, staying when others walk away.",
      "At WHCO, covenant is how the house stands. Between us and God. Between spiritual fathers and sons. Between spouses, generations, and ministry teams.",
    ],
    livesLike: [
      "Faithfulness in the small, unseen commitments.",
      "Honor across generations and leaders.",
      "Marriages, friendships, and teams that go the distance.",
    ],
    playlistUrl: "",
    playlistType: "auto",
  },
  obedience: {
    letter: "O",
    title: "Obedience",
    image: `${PILLAR_ART}/m6r55gw6_pillar-obedience.png`,
    scripture: {
      ref: "I John 2:3-5 NKJV",
      text: "\u201cNow by this we know that we know Him, if we keep His commandments. He who says, \u2018I know Him,\u2019 and does not keep His commandments, is a liar, and the truth is not in him. But whoever keeps His word, truly the love of God is perfected in him. By this we know that we are in Him.\u201d",
    },
    teaching: [
      "Obedience is the evidence that we know Him. John does not soften it: to say we know Jesus while ignoring His commandments is to live a lie. But whoever keeps His word — that is where the love of God is being perfected.",
      "The Spirit still speaks — through the Word, through prompting, through counsel, through peace. Our part is to say yes quickly and completely. Delayed obedience is disobedience; partial obedience is disobedience with polish.",
      "At WHCO, we choose obedience over comfort, over reputation, over convenience. Every yes to Jesus enlarges our capacity for the next yes — and it is the pathway to the miraculous.",
    ],
    livesLike: [
      "Immediate yes when the Holy Spirit speaks.",
      "Generosity when God says to give.",
      "Going to the person, the city, or the nation He points to.",
    ],
    playlistUrl: "",
    playlistType: "auto",
  },
};

const ORDER = ["worship", "holiness", "covenant", "obedience"];

// --- Playlist helpers ---
const detectProvider = (url) => {
  if (!url) return null;
  if (/spotify\.com/i.test(url)) return "spotify";
  if (/(youtube\.com|youtu\.be)/i.test(url)) return "youtube";
  return null;
};

const toEmbed = (url, type) => {
  const resolved = type && type !== "auto" ? type : detectProvider(url);
  if (!resolved || !url) return null;
  if (resolved === "spotify") {
    // Convert e.g. https://open.spotify.com/playlist/XXX → /embed/playlist/XXX
    if (url.includes("/embed/")) return url;
    return url.replace(/open\.spotify\.com\//i, "open.spotify.com/embed/");
  }
  if (resolved === "youtube") {
    const list = url.match(/[?&]list=([\w-]+)/);
    if (list) return `https://www.youtube.com/embed/videoseries?list=${list[1]}`;
    const vid = url.match(/(?:v=|youtu\.be\/)([\w-]+)/);
    if (vid) return `https://www.youtube.com/embed/${vid[1]}`;
  }
  return null;
};

const PlaylistCard = ({ pillar }) => {
  const embed = toEmbed(pillar.playlistUrl, pillar.playlistType);
  const provider = pillar.playlistType && pillar.playlistType !== "auto"
    ? pillar.playlistType
    : detectProvider(pillar.playlistUrl);

  return (
    <section className="py-24 bg-white" data-testid="pillar-playlist">
      <div className="max-w-4xl mx-auto px-8">
        <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">The Playlist</p>
        <h2 className="text-3xl md:text-5xl mb-10 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
          {pillar.title} <span className="italic">Playlist</span>
        </h2>
        <div className="border border-black/10 shadow-lg bg-white overflow-hidden" data-testid="pillar-playlist-card">
          {embed ? (
            <div
              className={provider === "spotify" ? "w-full" : "relative w-full"}
              style={provider === "spotify" ? undefined : { paddingTop: "56.25%" }}
            >
              <iframe
                src={embed}
                title={`${pillar.title} pillar playlist`}
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                allowFullScreen
                className={provider === "spotify" ? "w-full block" : "absolute inset-0 w-full h-full block"}
                style={provider === "spotify" ? { height: 380, border: 0 } : { border: 0 }}
                data-testid="pillar-playlist-iframe"
              />
            </div>
          ) : (
            <div className="p-12 md:p-16 text-center" data-testid="pillar-playlist-coming-soon">
              <div className="mx-auto mb-6 h-14 w-14 rounded-full border border-black/15 flex items-center justify-center text-black/30" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </div>
              <h3 className="text-2xl md:text-3xl mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                {pillar.title} Playlist
              </h3>
              <p className="text-black/60">Coming soon — Spotify / YouTube playlist</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const PillarDetailPage = () => {
  const { slug } = useParams();
  const pillar = PILLARS[slug];
  if (!pillar) return <Navigate to="/who-we-are" replace />;

  const idx = ORDER.indexOf(slug);
  const next = ORDER[(idx + 1) % ORDER.length];
  const nextPillar = PILLARS[next];

  return (
    <div className="pt-20 fade-in" data-testid={`pillar-page-${slug}`}>
      {/* Back link */}
      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-8 pt-8">
          <Link to="/who-we-are" className="heading-caps text-xs text-black/40 tracking-[0.3em] hover:text-black transition-colors" data-testid="pillar-back">
            &larr; Core Pillars
          </Link>
        </div>
      </div>

      {/* Hero — pillar slide only, no overlay text */}
      <section className="pt-8 pb-16 bg-white" data-testid="pillar-hero">
        <div className="max-w-6xl mx-auto px-8">
          <div className="border border-black/10 shadow-lg overflow-hidden bg-white">
            <img
              src={pillar.image}
              alt={`${pillar.title} — ${pillar.scripture.ref}`}
              loading="lazy"
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1152px"
              className="block w-full h-auto"
              data-testid="pillar-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Scripture anchor */}
      <section className="py-24 bg-neutral-50" data-testid="pillar-scripture">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-black/40 mb-6 tracking-[0.3em]">The Word</p>
          <blockquote className="text-2xl md:text-4xl leading-snug text-black/90" style={{ fontFamily: "Playfair Display, serif" }}>
            {pillar.scripture.text}
          </blockquote>
          <p className="mt-8 heading-caps text-xs text-red-700 tracking-[0.25em]" data-testid="pillar-scripture-ref">— {pillar.scripture.ref}</p>
        </div>
      </section>

      {/* Teaching */}
      <section className="py-24 bg-white" data-testid="pillar-teaching">
        <div className="max-w-4xl mx-auto px-8">
          <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">The Teaching</p>
          <h2 className="text-3xl md:text-5xl mb-10 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            Going <span className="italic">deeper</span> on {pillar.title.toLowerCase()}.
          </h2>
          <div className="space-y-6 text-lg text-black/70 leading-relaxed">
            {pillar.teaching.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </div>
      </section>

      {/* At WHCO, this looks like */}
      <section className="py-24 bg-black text-white" data-testid="pillar-lives-like">
        <div className="max-w-5xl mx-auto px-8">
          <p className="heading-caps text-xs text-white/40 mb-4 tracking-[0.2em]">At WHCO, this looks like</p>
          <h2 className="text-3xl md:text-5xl mb-14 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            Faith made <span className="italic">visible</span>.
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {pillar.livesLike.map((line, i) => (
              <div key={i} data-testid={`pillar-lives-item-${i}`}>
                <div className="text-red-500/80 text-sm mb-4 tracking-[0.25em] uppercase" style={{ fontFamily: "Playfair Display, serif" }}>0{i + 1}</div>
                <p className="text-white/70 leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Playlist */}
      <PlaylistCard pillar={pillar} />

      {/* Next-Step CTA */}
      <section className="py-24 bg-neutral-50" data-testid="pillar-next-step">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-black/40 mb-6 tracking-[0.3em]">Your Next Step</p>
          <h2 className="text-4xl md:text-6xl mb-8 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            Don&apos;t just read it. <span className="italic">Live it.</span>
          </h2>
          <p className="text-lg text-black/60 leading-relaxed mb-12 max-w-2xl mx-auto">
            Come stand with a house that is walking {pillar.title.toLowerCase()} out — in worship,
            in community, and in the streets of Lancaster and the nations.
          </p>
          <Link to="/contact?plan=1" className="btn-primary inline-block" data-testid="pillar-cta-visit">Plan Your Visit</Link>
        </div>
      </section>

      {/* Continue */}
      <section className="py-16 bg-black text-white border-t border-white/5" data-testid="pillar-next">
        <div className="max-w-5xl mx-auto px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="heading-caps text-xs text-white/40 mb-2 tracking-[0.3em]">Continue</p>
            <p className="text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
              {nextPillar.letter} &mdash; <span className="italic">{nextPillar.title}</span>
            </p>
          </div>
          <Link to={`/pillars/${next}`} className="inline-block px-8 py-3 border border-white/40 text-white heading-caps text-xs tracking-[0.25em] hover:bg-white hover:text-black transition-all duration-300" data-testid="pillar-next-link">
            Next Pillar &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PillarDetailPage;
